import { Navigate, Route, Routes } from 'react-router-dom';
import { features } from '@/config/features';
import { AppShell } from '@/components/layout/app-shell';
import { flatNavItems } from '@/components/layout/nav.config';
import { LoginPage } from '@/features/auth/login-page';
import { RegisterPage } from '@/features/auth/register-page';
import { ImpersonationPage } from '@/features/auth/impersonation-page';
import { InventoryPage } from '@/features/inventory/inventory-page';
import { InventoryReportPage } from '@/features/inventory-report/inventory-report-page';
import { MovementPage } from '@/features/movement/movement-page';
import { SettlementsPage } from '@/features/settlements/settlements-page';
import { SalesSummaryPage } from '@/features/sales-summary/sales-summary-page';
import { SalesReportPage } from '@/features/sales-report/sales-report-page';
import { RequireAuth } from '@/features/auth/require-auth';
import { AccountRequestsPage } from '@/features/account-requests/account-requests-page';
import { AdminPosLanesPage } from '@/features/admin-pos/admin-pos-page';
import { BusinessAccountsPage } from '@/features/business-accounts/business-accounts-page';
import { ConfigurationPage } from '@/features/configuration/configuration-page';
import { CustomersPage } from '@/features/customers/customers-page';
import { DeliveriesPage } from '@/features/deliveries/deliveries-page';
import { UsageAnalyticsPage } from '@/features/usage-analytics/usage-analytics-page';
import { GiftCardsPage } from '@/features/gift-cards/gift-cards-page';
import { BannerAdsPage } from '@/features/banner-ads/banner-ads-page';
import { InfoCardsPage } from '@/features/info-cards/info-cards-page';
import { LoyaltyPage } from '@/features/loyalty/loyalty-page';
import { DiscountUsagePage } from '@/features/pos-discounts/discount-usage-page';
import { PosDiscountsPage } from '@/features/pos-discounts/pos-discounts-page';
import { PromoCodesPage } from '@/features/promo-codes/promo-codes-page';
import { SponsorshipDetailPage } from '@/features/sponsorships/sponsorship-detail-page';
import { SponsorshipsPage } from '@/features/sponsorships/sponsorships-page';
import { EventsPage } from '@/features/events/events-page';
import { ManageBookingsPage } from '@/features/manage-bookings/manage-bookings-page';
import { ManageSubscriptionsPage } from '@/features/manage-subscriptions/manage-subscriptions-page';
import { OverviewPage } from '@/features/overview/overview-page';
import { PaymentReviewPage } from '@/features/payment-review/payment-review-page';
import { BookingPaymentHistoryPage } from '@/features/booking-payment-history/payment-history-page';
import { SalesPage } from '@/features/sales/sales-page';
import { SubscriptionPaymentHistoryPage } from '@/features/subscription-payment-history/payment-history-page';
import { SubscriptionSalesPage } from '@/features/subscription-sales/subscription-sales-page';
import { CategoriesPage } from '@/features/catalog/categories-page';
import { ItemMasterPage } from '@/features/item-master/item-master-page';
import { StaffPage } from '@/features/staff/staff-page';
import { TimesheetTypesPage } from '@/features/timesheets/timesheet-types-page';
import { TimesheetsPage } from '@/features/timesheets/timesheets-page';
import { PosLanesPage } from '@/features/pos/pos-lanes-page';
import { CashDrawerPage } from '@/features/pos/cash-drawer-page';
import { BusinessInfoPage } from '@/features/settings/business-info-page';
import { StoreOptionsPage } from '@/features/settings/store-options-page';
import { PaymentConfigPage } from '@/features/settings/payment-config-page';
import { ItemPricesPage } from '@/features/item-prices/item-prices-page';
import { ItemRegistrationPage } from '@/features/item-registration/item-registration-page';
import { SearchOptimizationPage } from '@/features/search-optimization/search-optimization-page';
import {
  AddonsPage,
  RequirementsPage,
  SidesPage,
} from '@/features/modifiers/modifiers-page';
import { DepartmentsPage } from '@/features/catalog/departments-page';
import { HostsPage } from '@/features/booking-setup/hosts-page';
import { LocationsPage } from '@/features/booking-setup/locations-page';
import { ServiceTypesPage } from '@/features/booking-setup/service-types-page';
import {
  BookingsHeatmapPage,
  SalesHeatmapPage,
} from '@/features/heatmaps/heatmap-page';
import { TimeSlotHostsPage } from '@/features/timeslot-hosts/timeslot-hosts-page';
import { MembershipGiftsPage } from '@/features/membership-gifts/membership-gifts-page';
import { MembershipPlansPage } from '@/features/memberships/membership-plans-page';
import { VoucherProductsPage } from '@/features/gift-vouchers/voucher-products-page';
import { ManageVouchersPage } from '@/features/gift-vouchers/manage-vouchers-page';
import { VoucherReportPage } from '@/features/gift-vouchers/voucher-report-page';
import { OrdersPage } from '@/features/orders/orders-page';
import { HomePage } from '@/pages/home/home-page';
import { DashboardPage } from '@/pages/dashboard/dashboard-page';
import { StoreDashboardPage } from '@/pages/dashboard/store-dashboard-page';
import { NotFoundPage, UnauthorizedPage } from '@/pages/error-pages';
import { PlaceholderPage } from '@/pages/placeholder-page';
import { PublicNotice } from '@/pages/public-notice';

/**
 * Pages that exist for real. Everything else in the nav resolves to the
 * placeholder, so the whole tree can be clicked through with no dead links.
 * Add a path here when its page lands, and give it its own <Route> below.
 */
const IMPLEMENTED = new Set([
  '/',
  '/dashboard',
  '/storedashboard',
  '/accounts',
  '/admin/pos',
  '/configuration',
  '/impersonation',
  '/inventory',
  '/reports/inventory',
  '/finance/movement',
  '/finance/settlements',
  '/finance/summary',
  '/finance/sales',
  '/customers',
  '/customers/requests',
  '/analytics/usage',
  '/giftcards',
  '/infocards',
  '/loyalty',
  '/promobanners',
  '/finance/pos/discounts',
  '/posdiscounts',
  '/promocodes',
  '/sponsorships',
  '/orders',
  '/booking/membership-plans',
  '/booking/events',
  '/booking/membership-gifts',
  '/booking/gift-vouchers',
  '/booking/manage/bookings',
  '/booking/manage/subscriptions',
  '/booking/manage/vouchers',
  '/booking/reports/vouchers',
  '/booking/manage/payment-review',
  '/booking/overview',
  '/booking/sales',
  '/booking/reports/purchaseshistory',
  '/booking/subscription-sales',
  '/booking/reports/subscriptionhistory',
  '/booking/reports/timeslot/hosts',
  '/booking/heatmap/sales',
  '/booking/heatmap/bookings',
  '/booking/locations',
  '/booking/hosts',
  '/booking/servicetypes',
  '/departments',
  '/sections',
  '/requirements',
  '/sides',
  '/addons',
  '/searchoptimization',
  '/itemprices',
  '/itemregistration',
  '/itemmaster',
  '/team',
  '/team/timesheet/types',
  '/team/timesheets',
  '/pos',
  '/pos/cashdrawer/report',
  '/business-info',
  '/store-options',
  '/payment-config',
  '/deliveries',
]);

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/forgot-password"
        element={
          <PublicNotice
            titleKey="auth.publicNotice.forgotTitle"
            bodyKey="auth.publicNotice.forgotBody"
          />
        }
      />

      {/* Everything below requires a session */}
      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          {/* A greeting and shortcuts, not the numbers: the dashboards are a
              click away for the accounts that read them, and everybody else
              signs in to do something specific. */}
          <Route index element={<HomePage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/storedashboard" element={<StoreDashboardPage />} />

          {/* The nav entry is gated on the capability, but a URL typed by hand
              is not, so the route carries the same check. */}
          <Route element={<RequireAuth permission="isCustomerService" />}>
            <Route path="/customers" element={<CustomersPage />} />
            {/* A client that does not take applications has no such page at
                all — not a placeholder, not an empty grid. The path is in
                IMPLEMENTED either way, so with the feature off it falls
                through to the 404 rather than to a stub. */}
            {features.accountRequests && (
              <Route
                path="/customers/requests"
                element={<AccountRequestsPage />}
              />
            )}
            <Route path="/orders" element={<OrdersPage />} />
          </Route>

          {/* Merged from the legacy /giftcards and /reports/giftcards, which
              were two components over the same endpoints. Create is gated
              inside the page, on the role the legacy menu split stood in for. */}
          {/* Blob-storage config. One page, one route; the three legacy paths
              land on it with the file preselected. */}
          <Route element={<RequireAuth permission="viewUsageAnalytics" />}>
            <Route path="/analytics/usage" element={<UsageAnalyticsPage />} />
          </Route>

          <Route element={<RequireAuth permission="isSuperAdmin" />}>
            <Route path="/configuration" element={<ConfigurationPage />} />
            <Route path="/admin/pos" element={<AdminPosLanesPage />} />
            <Route path="/accounts" element={<BusinessAccountsPage />} />
            <Route path="/impersonation" element={<ImpersonationPage />} />
          </Route>
          {(['server', 'app', 'manager'] as const).map((file) => (
            <Route
              key={file}
              path={`/configuration/${file}`}
              element={<Navigate to={`/configuration?file=${file}`} replace />}
            />
          ))}

          <Route element={<RequireAuth permission="isFinance" />}>
            <Route path="/giftcards" element={<GiftCardsPage />} />
          </Route>

          <Route element={<RequireAuth permission="isMarketing" />}>
            <Route path="/infocards" element={<InfoCardsPage />} />
            <Route path="/loyalty" element={<LoyaltyPage />} />
            <Route path="/promobanners" element={<BannerAdsPage />} />
            <Route path="/posdiscounts" element={<PosDiscountsPage />} />
            <Route path="/promocodes" element={<PromoCodesPage />} />
            <Route path="/sponsorships" element={<SponsorshipsPage />} />
            {/* Not a nav entry of its own; `useIsActive` keeps the
                Sponsorships row lit while a single one is open. */}
            <Route
              path="/sponsorships/:id"
              element={<SponsorshipDetailPage />}
            />
          </Route>

          <Route
            path="/booking/membership-plans"
            element={<MembershipPlansPage />}
          />
          <Route path="/booking/events" element={<EventsPage />} />
          <Route
            path="/booking/membership-gifts"
            element={<MembershipGiftsPage />}
          />
          <Route
            path="/booking/gift-vouchers"
            element={<VoucherProductsPage />}
          />
          <Route
            path="/booking/manage/vouchers"
            element={<ManageVouchersPage />}
          />
          <Route
            path="/booking/reports/vouchers"
            element={<VoucherReportPage />}
          />
          <Route
            path="/booking/manage/bookings"
            element={<ManageBookingsPage />}
          />
          <Route
            path="/booking/manage/subscriptions"
            element={<ManageSubscriptionsPage />}
          />
          <Route
            path="/booking/manage/payment-review"
            element={<PaymentReviewPage />}
          />
          <Route path="/booking/overview" element={<OverviewPage />} />
          <Route path="/booking/sales" element={<SalesPage />} />
          <Route
            path="/booking/subscription-sales"
            element={<SubscriptionSalesPage />}
          />
          <Route
            path="/booking/reports/subscriptionhistory"
            element={<SubscriptionPaymentHistoryPage />}
          />
          <Route
            path="/booking/reports/timeslot/hosts"
            element={<TimeSlotHostsPage />}
          />
          <Route path="/booking/heatmap/sales" element={<SalesHeatmapPage />} />
          <Route
            path="/booking/heatmap/bookings"
            element={<BookingsHeatmapPage />}
          />
          <Route path="/booking/locations" element={<LocationsPage />} />
          <Route path="/booking/hosts" element={<HostsPage />} />
          <Route path="/booking/servicetypes" element={<ServiceTypesPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route element={<RequireAuth permission="isFinance" />}>
            <Route
              path="/reports/inventory"
              element={<InventoryReportPage />}
            />
          </Route>
          <Route element={<RequireAuth permission="isFinance" />}>
            <Route path="/finance/movement" element={<MovementPage />} />
            <Route path="/finance/settlements" element={<SettlementsPage />} />
            <Route path="/finance/summary" element={<SalesSummaryPage />} />
            <Route path="/finance/sales" element={<SalesReportPage />} />
          </Route>
          {/* Sales was four pages that differed in their grouping. The old
              links land on the grouping — and the POS-only channel — they
              showed. */}
          <Route
            path="/finance/instore/sales"
            element={<Navigate to="/finance/sales?groupBy=day&channel=pos" replace />}
          />
          <Route
            path="/finance/instore/employees/sales"
            element={<Navigate to="/finance/sales?groupBy=employee&channel=pos" replace />}
          />
          <Route
            path="/finance/pos/sales"
            element={<Navigate to="/finance/sales?groupBy=lane&channel=pos" replace />}
          />
          <Route
            path="/finance/terminal"
            element={<Navigate to="/finance/settlements" replace />}
          />
          <Route
            path="/finance/pos/summary"
            element={<Navigate to="/finance/summary" replace />}
          />
          <Route path="/departments" element={<DepartmentsPage />} />
          <Route path="/sections" element={<CategoriesPage />} />
          <Route path="/requirements" element={<RequirementsPage />} />
          <Route path="/sides" element={<SidesPage />} />
          <Route path="/addons" element={<AddonsPage />} />
          <Route
            path="/searchoptimization"
            element={<SearchOptimizationPage />}
          />
          <Route path="/itemprices" element={<ItemPricesPage />} />
          <Route path="/itemmaster" element={<ItemMasterPage />} />
          <Route path="/team" element={<StaffPage />} />
          <Route
            path="/team/timesheet/types"
            element={<TimesheetTypesPage />}
          />
          <Route path="/team/timesheets" element={<TimesheetsPage />} />
          <Route path="/pos" element={<PosLanesPage />} />
          <Route
            path="/pos/cashdrawer/report"
            element={<CashDrawerPage />}
          />
          <Route path="/business-info" element={<BusinessInfoPage />} />
          <Route path="/store-options" element={<StoreOptionsPage />} />
          <Route path="/payment-config" element={<PaymentConfigPage />} />
          {/* One client works with third-party delivery providers. For the
              rest the path is a 404, as with account requests above; the
              page itself checks the store's own delivery switch. */}
          {features.thirdPartyDelivery && (
            <Route element={<RequireAuth permission="isDeliveryProvider" />}>
              <Route path="/deliveries" element={<DeliveriesPage />} />
            </Route>
          )}
          <Route
            path="/itemregistration"
            element={<ItemRegistrationPage />}
          />
          <Route
            path="/booking/reports/purchaseshistory"
            element={<BookingPaymentHistoryPage />}
          />
          {/* Its own page, not a tab of the Marketing one: the report's roles
              and the manager's do not overlap. */}
          <Route element={<RequireAuth permission="viewDiscountReport" />}>
            <Route
              path="/finance/pos/discounts"
              element={<DiscountUsagePage />}
            />
          </Route>

          {/* Movement was four pages that differed only in their grouping.
              The grouping is a filter now, and the level it lands on is the
              one the old link asked for. */}
          <Route
            path="/finance/divisions"
            element={<Navigate to="/finance/movement?level=division" replace />}
          />
          <Route
            path="/finance/departments"
            element={<Navigate to="/finance/movement?level=group" replace />}
          />
          <Route
            path="/finance/store/departments"
            element={<Navigate to="/finance/movement?level=department" replace />}
          />
          <Route
            path="/finance/store/categories"
            element={<Navigate to="/finance/movement?level=category" replace />}
          />

          {/* The legacy path filed this under "reports" although it lives in
              Maintenance and is not one. */}
          <Route
            path="/subscription/reports/gifts"
            element={<Navigate to="/booking/membership-gifts" replace />}
          />
          {/* The legacy path named the plan after the thing bought from it.
              Kept as a redirect so an old link still lands somewhere. */}
          <Route
            path="/booking/subscriptions"
            element={<Navigate to="/booking/membership-plans" replace />}
          />

          {/*
            Every nav entry resolves to something, so the sidebar can be clicked
            through end to end while pages are still being built. As a real page
            lands it is added to IMPLEMENTED and given its own <Route> above.
          */}
          {flatNavItems
            .filter((item) => !IMPLEMENTED.has(item.to))
            .map((item) => (
              <Route
                key={item.key}
                path={`${item.to}/*`}
                element={<PlaceholderPage />}
              />
            ))}

          <Route path="/profile" element={<PlaceholderPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
